import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { sumBy } from 'lodash';
// Import Hooks
import { useGTMEvent } from 'hooks/UseGTM';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { DashboardHttpService } from 'services/http';
import { getStartDate, getEndDate } from 'services/dashboard';
// Import UI Components
import { DatePickerInput, BusyLoader, DalColumnChart, Dropdown, Empty, ModuleTitle } from '@geneui/components';
// Import configs
import { depositCumulativeChartDataTimeIntervalConfigs } from './config';
// Import Constants
import { GACategoryEvents, l } from 'constants/common';

const { GADateFilterCategory } = GACategoryEvents;

interface ConversionTimeAnalyticsData {
    lineData: number[];
    columnData: number[];
    categories: number[];
}

const ConversionTimeAnalyticsChart = ({ parentDate }) => {
    const { doPostRequest } = useRequest();
    const { t } = useTranslation();
    const { logEvent } = useGTMEvent();
    const { partnerDateFormat } = usePartnerDateFormats();
    const { depositCumulativeChartDataTimeInterval } = depositCumulativeChartDataTimeIntervalConfigs(t);

    const [isLoading, setIsLoading] = useState(false);
    const [conversionTimeAnalyticsData, setConversionTimeAnalyticsData] =
        useState<Nullable<ConversionTimeAnalyticsData>>(null);
    const [date, setDate] = useState(parentDate);
    const [timeInterval, setTimeInterval] = useState(0);

    const { getDepositCumulativeChartDataRequest } = useCancelableRequests(
        { getDepositCumulativeChartDataRequest: DashboardHttpService.getDepositCumulativeChartData },
        { takeLatest: ['getDepositCumulativeChartDataRequest'] },
    );

    const getDepositCumulativeChartData = (date, timeInterval) => {
        setIsLoading(true);

        doPostRequest(getDepositCumulativeChartDataRequest.request, {
            requestBody: { DateStart: date[0], DateEnd: date[1], Interval: timeInterval },
            successCallback: (Data) => {
                const data = Data.reduce<ConversionTimeAnalyticsData>(
                    (acc, element) => {
                        acc.lineData.push(element.Total);
                        acc.columnData.push(element.Count);
                        acc.categories.push(element.TimePart);
                        return acc;
                    },
                    { lineData: [], columnData: [], categories: [] },
                );
                setConversionTimeAnalyticsData(data);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoading(false);
            }
        });
    };

    const handleDatePickerChange = (e) => {
        const date = [getStartDate(e[0]), getEndDate(e[1])];

        logEvent(
            GADateFilterCategory.name,
            GADateFilterCategory.events.conversionTimeAnalytics,
            GADateFilterCategory.labels.conversionTimeAnalytics,
            `${date[0]} - ${date[1]}`,
        );
        setDate(date);
    };

    const dropDownHandler = (values) => {
        setTimeInterval(values.value);
        getDepositCumulativeChartData(date, values.value);
    };

    useEffect(() => {
        getDepositCumulativeChartData(date, timeInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date[0], date[1]]);

    useEffect(() => {
        setDate(parentDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentDate]);

    return (
        <>
            <div className="charts-wrap-inner-head">
                <ModuleTitle title={t(l.ConversionTimeAnalytics)} />

                <div className="campaign-metrics-wrap-c-h-d">
                    <Dropdown
                        data={depositCumulativeChartDataTimeInterval}
                        placeholder={t(l.ConversionTimeInterval)}
                        labelAppearance="swap"
                        onChange={(values) => {
                            dropDownHandler(values);
                        }}
                        value={timeInterval}
                    />
                    <DatePickerInput.WithRange
                        label={t(l.TimePeriod)}
                        labelAppearance="swap"
                        onChange={handleDatePickerChange}
                        value={date}
                        format={partnerDateFormat}
                    />
                </div>
            </div>
            {isLoading ? (
                <BusyLoader spinnerSize="big" isBusy={true} />
            ) : sumBy(conversionTimeAnalyticsData?.lineData) === 0 ? (
                <Empty title={t(l.NoDataToDisplay)} />
            ) : (
                <div className="charts-wrap-inner-content">
                    <DalColumnChart
                        lineData={conversionTimeAnalyticsData?.lineData ?? []}
                        columnData={
                            conversionTimeAnalyticsData?.columnData.map((x, i) => ({
                                y: x,
                                name: conversionTimeAnalyticsData?.categories[i],
                            })) ?? []
                        }
                        categories={conversionTimeAnalyticsData?.categories ?? []}
                    />
                </div>
            )}
        </>
    );
};

ConversionTimeAnalyticsChart.propTypes = {
    parentDate: PropTypes.array.isRequired,
};

export default ConversionTimeAnalyticsChart;
